"use client";

// ── The account control for the Judge shell topbar (`EX-PUB11`) ─────────────
// The same avatar-dropdown shape as Playground's `PlaygroundToolbarAccount`.
// The trigger sits on the Judge bar, which is dark on every Judge page since
// `EX-PUB19`; the dropdown itself stays light, because it is a menu over the
// page, not part of the bar.

import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { LayoutDashboard, LogOut, User } from "lucide-react";
import { useTranslations } from "next-intl";
import { useAuth } from "@/components/providers/auth-provider";
import { Button } from "@/components/ui/button";
import { cfImageUrl } from "@/lib/cloudflare-image";
import { useSignInHref } from "@/lib/use-sign-in-href";
import { toPublicProfileSlug, userInitials } from "@/features/playground/lib/public-profile-slug";

const menuItemClass =
  "flex cursor-pointer select-none items-center gap-2 rounded-lg px-3 py-2 text-sm text-neutral-600 outline-none transition-colors duration-150 ease-out data-[highlighted]:bg-neutral-50 data-[highlighted]:text-neutral-900";

export function JudgeAccountMenu({ locale }: { locale: string }) {
  const t = useTranslations("nav");
  const tShell = useTranslations("judge.shell");
  const router = useRouter();
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  // `RV-20`: Log in comes back to the page the bar is on.
  const signInHref = useSignInHref(locale);

  if (isLoading) {
    return <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-white/10" aria-hidden />;
  }

  if (!isAuthenticated || !user) {
    // From `sm`, taking taps over 44 px; below it Log in is in the bar's menu (`JudgeShellTopbar`).
    return (
      <Link
        href={signInHref}
        className="relative hidden h-9 shrink-0 items-center justify-center rounded-lg border border-primary/60 px-3 text-sm font-semibold text-accent-peach transition-colors duration-200 ease-out before:absolute before:inset-x-0 before:-inset-y-1.5 before:content-[''] hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-950 sm:inline-flex"
      >
        {t("login")}
      </Link>
    );
  }

  const initials = userInitials(user);
  const avatarUrl = user.avatarImageId ? cfImageUrl(user.avatarImageId, "avatar") : "";
  const profileSlug = toPublicProfileSlug(user);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        {/* A 32 px circle that takes taps over 44 px (`avatar`), so a phone's bar keeps its room. */}
        <Button type="button" variant="ghostDark" size="avatar" aria-label={tShell("accountMenuOpen")} className="group shrink-0">
          <span className="flex h-full w-full items-center justify-center overflow-hidden rounded-full border border-white/15 bg-primary text-xs font-bold text-white transition-opacity duration-150 ease-out group-hover:opacity-90">
            {avatarUrl ? (
              <Image src={avatarUrl} alt="" width={32} height={32} className="h-full w-full object-cover" />
            ) : (
              <span aria-hidden>{initials}</span>
            )}
          </span>
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-[220] min-w-[220px] rounded-[12px] border border-neutral-200 bg-white p-1 shadow-[0_8px_30px_rgba(0,0,0,0.06)]"
          sideOffset={8}
          align="end"
          collisionPadding={8}
        >
          <div className="px-3 py-2.5">
            <p className="truncate text-sm font-semibold text-neutral-900">{user.name}</p>
            <p className="truncate text-xs text-neutral-400">{user.email}</p>
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-neutral-200" />
          <DropdownMenu.Item asChild>
            <Link href={`/${locale}/dashboard`} className={menuItemClass}>
              <LayoutDashboard className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden />
              {t("dashboard")}
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <Link href={`/${locale}/u/${encodeURIComponent(profileSlug)}`} className={menuItemClass}>
              <User className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden />
              {t("profile")}
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="my-1 h-px bg-neutral-200" />
          <DropdownMenu.Item
            className="flex cursor-pointer select-none items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-700 outline-none transition-colors duration-150 ease-out data-[highlighted]:bg-red-50"
            onSelect={(e) => {
              e.preventDefault();
              void logout().then(() => router.refresh());
            }}
          >
            <LogOut className="h-4 w-4 shrink-0" aria-hidden />
            {t("logout")}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
